import type { RealtimeEvent } from '@/types/email';

const MAX_FLUSH_RETRIES = 3;

export class RealtimeEventBatcher {
  private pendingEvents: RealtimeEvent[] = [];
  private batchTimer: ReturnType<typeof setTimeout> | null = null;
  private maxDelayTimer: ReturnType<typeof setTimeout> | null = null;
  private processor: (events: RealtimeEvent[]) => void;
  private batchWindow: number;
  private maxDelay: number;
  private flushFailures = 0;

  constructor(processor: (events: RealtimeEvent[]) => void, batchWindow = 50, maxDelay = 500) {
    this.processor = processor;
    this.batchWindow = batchWindow;
    this.maxDelay = maxDelay;
  }

  addEvent(event: RealtimeEvent) {
    this.pendingEvents.push(event);

    // Reset debounce timer
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
    }

    // Start max delay timer on first event in batch
    if (!this.maxDelayTimer) {
      this.maxDelayTimer = setTimeout(() => {
        this.flush();
      }, this.maxDelay);
    }

    // Schedule batch processing
    this.batchTimer = setTimeout(() => {
      this.flush();
    }, this.batchWindow);
  }

  flush() {
    if (this.pendingEvents.length === 0) return;

    if (this.maxDelayTimer) {
      clearTimeout(this.maxDelayTimer);
      this.maxDelayTimer = null;
    }
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
    }

    const events = [...this.pendingEvents];
    this.pendingEvents = [];
    this.batchTimer = null;

    // Process all events in single batch, re-queue on failure
    try {
      this.processor(events);
      this.flushFailures = 0;
    } catch (error) {
      this.flushFailures += 1;
      if (this.flushFailures > MAX_FLUSH_RETRIES) {
        // A poison event would otherwise be re-queued forever, piling every
        // later event up behind it (E11). Drop the batch (logged) and let the
        // periodic/visibility resync reconcile whatever it represented.
        console.error(
          `[RealtimeEventBatcher] dropping ${events.length} event(s) after ${MAX_FLUSH_RETRIES} failed retries`,
          error
        );
        this.flushFailures = 0;
        return;
      }
      console.error('[RealtimeEventBatcher] flush failed, re-queuing events', error);
      // Re-queue events that failed to process and schedule a retry
      this.pendingEvents = [...events, ...this.pendingEvents];
      if (!this.batchTimer) {
        this.batchTimer = setTimeout(() => {
          this.flush();
        }, this.batchWindow * 2);
      }
    }
  }

  destroy() {
    if (this.batchTimer) {
      clearTimeout(this.batchTimer);
    }
    if (this.maxDelayTimer) {
      clearTimeout(this.maxDelayTimer);
      this.maxDelayTimer = null;
    }
    this.flush(); // Process any remaining events
  }
}
